import { useMutation, useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { FulfillmentType } from '@/types/app'

export interface StorefrontItem {
  id: string
  category_id: string | null
  name: string
  regional_name: string | null
  description: string | null
  price: number
  images: string[]
  dietary_flags: string[]
  allergens: string[]
}

export interface StorefrontCategory {
  id: string
  name: string
  display_order: number
}

export interface StorefrontBranding {
  name: string
  logo_url: string | null
  brand_colour_primary: string | null
}

export interface StorefrontCatalog {
  restaurant: StorefrontBranding
  categories: StorefrontCategory[]
  items: StorefrontItem[]
}

export interface CheckoutCartLine {
  menu_item_id: string
  quantity: number
}

export interface CheckoutPayload {
  slug: string
  client_name: string
  client_phone: string
  client_email?: string
  fulfillment_type?: FulfillmentType
  requested_date?: string
  venue_address?: string
  notes?: string
  items: CheckoutCartLine[]
}

async function callStorefrontFunction<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('public-storefront', { body })
  if (error) {
    type FnError = { context?: Response }
    const context = (error as FnError).context
    const code = context ? ((await context.clone().json().catch(() => null)) as { error?: string } | null)?.error : null
    throw new Error(code ?? error.message)
  }
  return data as T
}

// Public, unauthenticated catalog for a storefront — resolved by the
// restaurant's slug (works identically whether it's reached via
// /store/:slug on any domain, or later via a hostname that resolves to the
// same slug once a custom domain is actually pointed here).
export function useStorefrontCatalog(slug: string | undefined) {
  return useQuery({
    queryKey: ['public-storefront', 'catalog', slug],
    queryFn: () => callStorefrontFunction<StorefrontCatalog>({ action: 'catalog', slug }),
    enabled: !!slug,
    staleTime: 60_000,
  })
}

export function useStorefrontCheckout() {
  return useMutation({
    mutationFn: (payload: CheckoutPayload) =>
      callStorefrontFunction<{ success: true; order_number: string }>({ action: 'checkout', ...payload }),
  })
}
